// MessageStatusTimeline.tsx - Hover-based delivery status timeline
import React, { useState, useEffect } from 'react';
import { Clock, Check, CheckCheck, X, AlertCircle } from 'lucide-react';
import moment from 'moment';
import './MessageStatusTimeline.css';
import logger from '../../../utils/logger';

interface StatusLog {
  status: string;
  timestamp: string;
}

interface MessageError {
  code: number;
  title: string;
  message: string;
  error_data?: {
    details: string;
  };
}

interface MessageStatusTimelineProps {
  status?: string;
  statusLogs?: StatusLog[];
  messageId: string;
  onRetry?: () => void;
  error?: MessageError[];
}

const MessageStatusTimeline: React.FC<MessageStatusTimelineProps> = ({
  status,
  statusLogs,
  messageId,
  onRetry,
  error
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // 🔍 Debug logging for error prop
  useEffect(() => {
    if (status === 'failed' || (statusLogs && statusLogs.some(log => log.status === 'failed'))) {
      logger.log('🔴 MessageStatusTimeline - Failed message detected:', {
        messageId,
        status,
        statusLogsCount: statusLogs?.length || 0,
        hasError: !!error,
        errorLength: error?.length || 0,
        errorData: error
      });
    }
  }, [messageId, status, statusLogs, error]);

  // Get the current status from status prop or latest statusLog
  const getCurrentStatus = (): string => {
    if (status) return status;
    if (statusLogs && statusLogs.length > 0) {
      const sortedLogs = [...statusLogs].sort((a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      return sortedLogs[0].status;
    }
    return 'sending';
  };

  const currentStatus = getCurrentStatus();

  // Get icon for status with better visibility
  const getStatusIcon = (statusType: string, size: number = 16) => {
    const iconProps = { size, strokeWidth: 2.5 };

    switch (statusType) {
      case 'sending':
        return <Clock {...iconProps} />;
      case 'sent':
        return <Check {...iconProps} />;
      case 'delivered':
        return <CheckCheck {...iconProps} />;
      case 'read':
        return <CheckCheck {...iconProps} className="status-read-icon" />;
      case 'failed':
        return <X {...iconProps} strokeWidth={3} />;
      default:
        return <Clock {...iconProps} />;
    }
  };

  // Get color for status - maximum visibility
  const getStatusColor = (statusType: string): string => {
    switch (statusType) {
      case 'sending':
        return '#ffffff'; // Pure white for visibility
      case 'sent':
        return '#ffffff'; // Pure white for visibility
      case 'delivered':
        return '#ffffff'; // Pure white for visibility
      case 'read':
        return '#10b981'; // Bright green, highly visible
      case 'failed':
        return '#ef4444'; // Bright red
      default:
        return '#ffffff';
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string): string => {
    const now = moment();
    const msgTime = moment(timestamp);

    if (now.diff(msgTime, 'days') === 0) {
      // Today - show time only
      return msgTime.format('HH:mm:ss');
    } else if (now.diff(msgTime, 'days') === 1) {
      // Yesterday
      return `Yesterday ${msgTime.format('HH:mm:ss')}`;
    } else if (now.diff(msgTime, 'days') < 7) {
      // Within a week
      return msgTime.format('ddd HH:mm:ss');
    } else {
      // Older
      return msgTime.format('MMM D, HH:mm:ss');
    }
  };

  // Get display label for status
  const getStatusLabel = (statusType: string): string => {
    switch (statusType) {
      case 'sending':
        return 'Sending';
      case 'sent':
        return 'Sent';
      case 'delivered':
        return 'Delivered';
      case 'read':
        return 'Read';
      case 'failed':
        return 'Failed';
      case 'received':
        return 'Received';
      default:
        return statusType;
    }
  };

  // Sort status logs chronologically
  const sortedStatusLogs = statusLogs
    ? [...statusLogs].sort((a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )
    : [];

  // Don't show timeline for received messages (incoming)
  if (currentStatus === 'received') {
    return null;
  }

  return (
    <div
      className="message-status-timeline-wrapper"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Compact status indicator */}
      {currentStatus === 'failed' && onRetry ? (
        <div
          className="failed-message-indicator"
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          title={error && error.length > 0 ? "Hover for details • Click to retry" : "Click to retry sending this message"}
          onClick={(e) => {
            e.stopPropagation();
            onRetry();
          }}
        >
          <X size={16} style={{ color: '#ef4444', strokeWidth: 3 }} />
          <span style={{ fontSize: '11px', fontWeight: 600 }}>Failed - Tap to retry</span>
          {error && error.length > 0 && (
            <AlertCircle size={14} style={{ color: '#ef4444', marginLeft: '4px' }} />
          )}
        </div>
      ) : (
        <div className="message-status-compact">
          <span
            className={`status-icon status-${currentStatus}`}
            style={{
              color: getStatusColor(currentStatus),
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '50%',
              padding: '3px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            {getStatusIcon(currentStatus)}
          </span>
        </div>
      )}

      {/* Hover timeline - show when hovered AND (has status logs OR has error) */}
      {isHovered && (sortedStatusLogs.length > 0 || (currentStatus === 'failed' && error && error.length > 0)) && (
        <div className="message-status-timeline-expanded">
          <div className="timeline-header">
            <span className="timeline-title">Delivery Timeline</span>
          </div>

          <div className="timeline-content">
            {sortedStatusLogs.map((log, index) => {
              const isLast = index === sortedStatusLogs.length - 1;
              const statusColor = getStatusColor(log.status);

              return (
                <div
                  key={`${messageId}-${log.status}-${index}`}
                  className={`timeline-item ${isLast ? 'timeline-item-current' : ''}`}
                >
                  <div className="timeline-marker">
                    <div
                      className="timeline-dot"
                      style={{
                        backgroundColor: statusColor,
                        boxShadow: isLast ? `0 0 10px ${statusColor}` : 'none',
                        border: `2px solid ${isLast ? statusColor : 'rgba(255, 255, 255, 0.2)'}`
                      }}
                    >
                      {isLast && (
                        <span style={{ color: '#ffffff', display: 'flex', alignItems: 'center' }}>
                          {getStatusIcon(log.status, 12)}
                        </span>
                      )}
                    </div>
                    {index < sortedStatusLogs.length - 1 && (
                      <div className="timeline-line" />
                    )}
                  </div>

                  <div className="timeline-details">
                    <div className="timeline-status">
                      {getStatusLabel(log.status)}
                    </div>
                    <div className="timeline-timestamp">
                      {formatTimestamp(log.timestamp)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Error reason section for failed messages */}
          {currentStatus === 'failed' && error && error.length > 0 && (
            <div className="timeline-error-section">
              <div className="timeline-error-header">
                <AlertCircle size={14} style={{ color: '#ef4444' }} />
                <span>Failure Reason</span>
              </div>
              <div className="timeline-error-content">
                <div className="timeline-error-title">
                  {error[0].title}
                </div>
                {error[0].error_data?.details && (
                  <div className="timeline-error-details">
                    {error[0].error_data.details}
                  </div>
                )}
                <div className="timeline-error-code">
                  Error Code: {error[0].code}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageStatusTimeline;
